import { iconEnumToString } from "@storytell/bsky-icons";
import {
  AssociateLabelRequestConfig,
  CreateLabelRequestConfig,
  DisassociateLabelRequestConfig,
  useStorytellClient,
} from "@storytell/client";
import type { Icons } from "@storytell/cp-client";
import type { AssetId, Label, LabelAssociation, LabelId } from "@storytell/types";
import { type Component, createMemo, createSignal } from "solid-js";
import { useWire } from "~/wire";
import { LabelPicker } from "./LabelPicker";

export const AssetLabelPicker: Component<{
  assetId: string;
  assetLabels: Label[];
  onLabelCreated: (label: Label) => void;
  mount?: Node;
  onClose?: () => void;
}> = (props) => {
  const wire = useWire();
  const [search, setSearch] = createSignal("");
  const storytellClient = useStorytellClient();

  const labels = createMemo(() => wire.labels.search(search()));

  const onCreateLabel = async (data: {
    name: string;
    description: string;
    icon: Icons.Icon;
    color: string;
  }) => {
    try {
      const client = useStorytellClient();
      const request = CreateLabelRequestConfig.withBearerToken(wire.auth.token());
      const result = await client.fetch(request, undefined, {
        name: data.name,
        description: data.description,
        icon: iconEnumToString(data.icon),
        color: data.color,
      });

      if (result.isOk()) {
        const label = result.value;
        wire.labels.set(label.id as LabelId, { ...label, userId: wire.auth.user()?.id || "" });
        await onAddLabelToAsset(label.id);
        props.onLabelCreated(label);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const onAddLabelToAsset = async (labelId: LabelId) => {
    wire.rels.addLabelToAsset(props.assetId as AssetId, labelId);

    try {
      const requestConfig = AssociateLabelRequestConfig.withBearerToken(wire.auth.token());
      const result = await storytellClient.fetch(requestConfig, undefined, [
        { labelId, resourceId: props.assetId, resourceType: "asset" },
      ]);

      if (result.isErr()) {
        wire.rels.removeLabelFromAsset(props.assetId as AssetId, labelId);
        return;
      }

      result.value.forEach((assoc) => wire.labels.onLabelAssociated(assoc as LabelAssociation));
    } catch (error) {
      console.error(error);
      wire.rels.removeLabelFromAsset(props.assetId as AssetId, labelId);
    }
  };

  const onRemoveLabelFromAsset = async (labelId: LabelId) => {
    const existing = wire.rels.assetHasLabel(props.assetId as AssetId, labelId);
    if (!existing) return;

    wire.rels.removeLabelFromAsset(props.assetId as AssetId, labelId);
    try {
      const requestConfig = DisassociateLabelRequestConfig.withBearerToken(wire.auth.token());
      const associationId = wire.labels.getAssociation(labelId, props.assetId)?.id;
      if (!associationId) return;
      const result = await storytellClient.fetch(requestConfig, undefined, [
        {
          associationId,
        },
      ]);

      if (result.isErr()) {
        wire.rels.addLabelToAsset(props.assetId as AssetId, labelId);
        return;
      }

      wire.labels.onLabelDisassociated(associationId);
    } catch (error) {
      console.error(error);
      wire.rels.addLabelToAsset(props.assetId as AssetId, labelId);
    }
  };

  return (
    <LabelPicker
      search={search()}
      setSearch={setSearch}
      labels={labels()}
      selectedLabels={props.assetLabels.map((l) => l.id)}
      onSelectLabel={(label) => onAddLabelToAsset(label.id)}
      onDeselectLabel={(label) => onRemoveLabelFromAsset(label.id)}
      onCreateLabel={onCreateLabel}
      mount={props.mount}
      onClose={props.onClose}
    />
  );
};
